import { useCallback, useContext, useEffect, useState } from 'react';
import { PortalContext } from '../../../context/portal-context';
import { ShimBackgroundTypeV2 } from '@volkswagen-onehub/layer-manager';
import React from 'react';
import { LayerComponent } from '../detail-layer/layer-component';
import { useMyServicesTracker } from './use-my-services-tracker';
import { GetPersonUser, Vehicle } from '@esportal/portal-service';
import { defaultServiceLayer, ServiceLayer, ServiceType } from '../models/services-layer-types';
import { getServiceLayerData } from '../detail-layer/get-service-layer-data';

export const useMyServicesLayer = ({ vehicle, userData }: { vehicle: Vehicle; userData: GetPersonUser }) => {
	const portalContext = useContext(PortalContext);
	const [isLayerOpen, setIsLayerOpen] = useState(false);
	const [servicesLayer, setServicesLayer] = useState<ServiceLayer>(defaultServiceLayer);

	const { trackCloseMyServicesLayerClick, trackLoadMyServicesLayer, trackOpenMyServicesLayerClick } =
		useMyServicesTracker();

	const handleLayerOpen = (type: ServiceType, ctaLabel?: string) => {
		const layer = getServiceLayerData(type, vehicle, portalContext);
		setServicesLayer(layer);
		setIsLayerOpen(true);
		trackOpenMyServicesLayerClick(vehicle, layer.id, layer.title, ctaLabel ?? 'Saber más');
	};

	useEffect(() => {
		if (isLayerOpen) {
			trackLoadMyServicesLayer(vehicle, servicesLayer.id, servicesLayer.title);
		}
	}, [isLayerOpen, servicesLayer, vehicle, trackLoadMyServicesLayer]);

	const handleLayerClose = useCallback(() => {
		setIsLayerOpen(false);
		trackCloseMyServicesLayerClick(servicesLayer.id, servicesLayer.title);
	}, [servicesLayer, trackCloseMyServicesLayerClick]);

	const renderMyServicesLayer = useCallback(() => {
		return (
			<PortalContext.Provider value={portalContext}>
				<LayerComponent servicesLayer={servicesLayer} vehicle={vehicle} userData={userData} />
			</PortalContext.Provider>
		);
	}, [portalContext, servicesLayer, vehicle, userData]);

	useEffect(() => {
		if (!isLayerOpen) return;

		const layer = portalContext.layerManager.openContentLayerV2(
			renderMyServicesLayer,
			{},
			{
				shimBackgroundType: ShimBackgroundTypeV2.OPAQUE,
				closeButtonLabel: 'Close services layer',
				contentHeadline: servicesLayer.header,
				onClose: handleLayerClose,
			},
		);

		return () => {
			layer.close();
		};
	}, [isLayerOpen, portalContext.layerManager, renderMyServicesLayer, servicesLayer, handleLayerClose]);

	return {
		handleLayerOpen,
	};
};
